import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
  FilterSelect,
  HpButton,
  HpCardHeader,
  HpField,
  HpTag,
  PageHeading,
  Segmented,
  Toolbar,
  ToolbarSpacer,
} from '@hp/ui';
import { addDays, isoDateInZone } from '@hp/domain';
import { useCan } from '../api/me';
import { ErrorAlert } from '../components/error-alert';
import { Disclosure } from '../components/ui/disclosure';
import { BrutTable, BrutTheadRow } from '../components/ui/table';
import { CohortTable, type CohortRow } from '../features/performance/cohort-table';
import { HistoryPanel } from '../features/performance/history-panel';
import { BudgetEditor } from '../features/performance/budget-editor';
import { MediaPanel } from '../features/performance/media-panel';
import { useBuildStatus, useCohort, useStageMap } from '../features/performance/queries';
import { SnapshotPanel } from '../features/performance/snapshot-panel';
import { fmtInt } from '../lib/format';

const PRESETS = ['thisYear', 'last30', 'last90', 'lastYear', 'custom'] as const;
type Preset = (typeof PRESETS)[number];
const GROUPS = ['channel', 'pipeline', 'project', 'campaign'] as const;
type GroupBy = (typeof GROUPS)[number];
const RANKS = [1, 2, 3, 4, 5, 6, 7] as const;

function presetPeriod(preset: Preset, today: string): { from: string; to: string } {
  const year = today.slice(0, 4);
  switch (preset) {
    case 'thisYear':
      return { from: `${year}-01-01`, to: today };
    case 'last30':
      return { from: addDays(today, -29), to: today };
    case 'last90':
      return { from: addDays(today, -89), to: today };
    case 'lastYear': {
      const y = Number(year) - 1;
      return { from: `${y}-01-01`, to: `${y}-12-31` };
    }
    case 'custom':
      return { from: `${year}-01-01`, to: today };
  }
}

/** `/marketing/performance/data` — snapshot, deal history, media imports and the detailed cohort table behind the dashboard. */
export default function PerformanceData() {
  const { t } = useTranslation(['performance', 'common']);
  const navigate = useNavigate();
  const canRun = useCan()('performance.manage');
  const today = isoDateInZone(new Date());
  const [preset, setPreset] = useState<Preset>('thisYear');
  const [custom, setCustom] = useState(() => presetPeriod('thisYear', today));
  const [groupBy, setGroupBy] = useState<GroupBy>('channel');
  const period = preset === 'custom' ? custom : presetPeriod(preset, today);
  const periodValid = period.from <= period.to && period.to <= today;

  const status = useBuildStatus();
  const hasSnapshot = Boolean(status.data?.published);
  const cohort = useCohort({ from: period.from, to: period.to }, hasSnapshot && periodValid);
  const stageMap = useStageMap();

  const rows = useMemo<CohortRow[]>(() => {
    const data = cohort.data;
    if (!data) return [];
    switch (groupBy) {
      case 'channel':
        return data.channels.map((c) => ({
          key: c.channel,
          label: t(`channel.${c.channel}`),
          detail: c.sources
            .slice(0, 4)
            .map((s) => `${s.label ?? t('noSource')} ${fmtInt(s.leads)}`)
            .join(' · '),
          metrics: c.metrics,
          spend: c.spend ?? null,
        }));
      case 'pipeline':
        return data.pipelines.map((p) => ({
          key: String(p.pipelinePdId ?? 'none'),
          label: p.name ?? String(p.pipelinePdId ?? '—'),
          metrics: p.metrics,
        }));
      case 'project':
        return data.projects.map((p) => ({
          key: p.projectOption ?? 'none',
          label: p.label ?? t('noProject'),
          metrics: p.metrics,
        }));
      case 'campaign':
        return data.campaigns.map((c) => ({
          key: c.campaignOption ?? 'none',
          label: c.label ?? (c.campaignOption === null ? t('noCampaign') : c.campaignOption),
          metrics: c.metrics,
          spend: c.spend ?? null,
        }));
    }
  }, [cohort.data, groupBy, t]);

  const population = status.data?.population;
  const showSpend = groupBy === 'channel' || groupBy === 'campaign';

  return (
    <div className="bo-sections">
      <PageHeading
        title={t('dataTitle')}
        description={t('dataDescription')}
        actions={
          <span className="bo-row">
            <HpButton variant="secondary" icon="arrowLeft" onClick={() => void navigate('/marketing/performance')}>
              {t('dataBack')}
            </HpButton>
            <HpButton variant="secondary" icon="link" onClick={() => void navigate('/marketing/performance/mappings')}>
              {t('mapping.open')}
            </HpButton>
          </span>
        }
      />

      <div className="bo-history-grid">
        <SnapshotPanel status={status.data} canRun={canRun} />
        <HistoryPanel pipelineIds={population?.pipelineIds ?? []} since={population?.since ?? ''} canRun={canRun} />
      </div>

      <MediaPanel canRun={canRun} />

      <BudgetEditor canManage={canRun} />

      <section className="hp-card">
        <HpCardHeader title={t('cohort.title')} subtitle={t('cohort.subtitle')} icon="chart">
          {cohort.data && (
            <span className="bo-muted bo-mono">
              {t('table.period', {
                from: period.from,
                to: period.to,
                days: cohort.data.days,
                snapshot: cohort.data.snapshotId,
              })}
            </span>
          )}
        </HpCardHeader>
        <Toolbar className="bo-toolbar">
          <Segmented
            ariaLabel={t('filters.period')}
            value={preset}
            options={PRESETS.map((p) => ({ value: p, label: t(`filters.${p}`) }))}
            onChange={(p) => {
              setPreset(p);
              if (p !== 'custom') setCustom(presetPeriod(p, today));
            }}
          />
          {preset === 'custom' && (
            <>
              <HpField label={t('filters.from')} className="bo-field--narrow">
                <input
                  type="date"
                  value={custom.from}
                  max={today}
                  onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))}
                />
              </HpField>
              <HpField label={t('filters.to')} className="bo-field--narrow">
                <input
                  type="date"
                  value={custom.to}
                  max={today}
                  onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))}
                />
              </HpField>
            </>
          )}
          <ToolbarSpacer />
          <FilterSelect
            value={groupBy}
            aria-label={t('filters.groupAria')}
            onChange={(e) => setGroupBy(e.target.value as GroupBy)}
          >
            {GROUPS.map((g) => (
              <option key={g} value={g}>
                {t(`filters.${g}`)}
              </option>
            ))}
          </FilterSelect>
        </Toolbar>
        {!periodValid && <p className="hp-card__body bo-hint bo-hint--danger">{t('filters.invalid')}</p>}
        <ErrorAlert error={cohort.error} className="bo-alert--inset" />
        {!hasSnapshot ? (
          <p className="hp-card__body bo-muted">{t('snapshot.none')}</p>
        ) : cohort.isLoading ? (
          <p className="hp-card__body bo-muted">{t('common:actions.loading')}</p>
        ) : (
          cohort.data && (
            <CohortTable
              rows={rows}
              totals={cohort.data.totals}
              label={t('table.label')}
              showSpend={showSpend}
              totalsSpend={groupBy === 'channel' ? cohort.data.spend : null}
            />
          )
        )}
        {cohort.data && cohort.data.totals.reservedForeign > 0 && (
          <p className="hp-card__body bo-hint">{t('table.foreign', { count: cohort.data.totals.reservedForeign })}</p>
        )}
        {cohort.data && cohort.data.totals.unconverted > 0 && (
          <p className="hp-card__body bo-hint bo-hint--danger">
            {t('table.unconverted', { count: cohort.data.totals.unconverted })}
          </p>
        )}
        {groupBy === 'campaign' && cohort.data && cohort.data.unmappedSpend.costCzk > 0 && (
          <p className="hp-card__body bo-hint">
            {t('table.unmappedSpend', { czk: fmtInt(Math.round(cohort.data.unmappedSpend.costCzk)) })}
          </p>
        )}
        <p className="hp-card__body bo-hint">{t('attributionNote')}</p>
        {showSpend && <p className="hp-card__body bo-hint">{t('spendNote')}</p>}
      </section>

      {stageMap.data && (
        <Disclosure title={t('stageMap.title', { version: stageMap.data.version })} flush>
          <BrutTable>
            <BrutTheadRow>
              <th>{t('stageMap.pipeline')}</th>
              <th>{t('stageMap.stage')}</th>
              <th>{t('stageMap.rank')}</th>
            </BrutTheadRow>
            <tbody>
              {stageMap.data.entries.map((e) => (
                <tr key={`${e.pipelinePdId}-${e.stagePdId}`}>
                  <td>
                    {e.pipelineName ?? e.pipelinePdId} <small className="bo-mono bo-muted">{e.pipelinePdId}</small>
                  </td>
                  <td>
                    {e.stageName ?? e.label} <small className="bo-mono bo-muted">{e.stagePdId}</small>
                  </td>
                  <td>
                    <span className="bo-row">
                      {t(`rank.${RANKS.find((r) => r === e.rank) ?? 1}`)}
                      {e.isNoContact && <HpTag tone="neutral">{t('stageMap.noContact')}</HpTag>}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </BrutTable>
        </Disclosure>
      )}
    </div>
  );
}
